import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api, { uploadQrMenuImage, getFileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ImagePlus } from "lucide-react";

const emptyForm = {
  title: "",
  description: "",
  imageId: null,
  image_url: "",
  is_active: true,
  startDate: "",
  endDate: "",
  selectedProductIds: [],
};

const getBusinessSubDomain = (user) =>
  user?.businessSubDomain ??
  user?.BusinessSubDomain ??
  user?.subDomain ??
  user?.SubDomain ??
  user?.business?.subDomain ??
  user?.business?.SubDomain ??
  "";

const normalizeIds = (ids = []) =>
  Array.isArray(ids) ? ids.filter(Boolean).map((id) => String(id)) : [];

const normalizeNumberIds = (ids = []) =>
  Array.isArray(ids)
    ? ids.filter((id) => id !== null && id !== undefined && id !== "").map(Number)
    : [];

const getPromotionIdFromResponse = (res) =>
  res?.data?.id ??
  res?.data?.Id ??
  res?.data?.promotionId ??
  res?.data?.PromotionId ??
  res?.data?.data?.id ??
  res?.data?.data?.Id ??
  res?.data?.data?.promotionId ??
  res?.data?.data?.PromotionId ??
  null;

const getProductCategoryIds = (product = {}) => {
  if (Array.isArray(product.categoryIds) && product.categoryIds.length) {
    return normalizeNumberIds(product.categoryIds);
  }

  if (Array.isArray(product.CategoryIds) && product.CategoryIds.length) {
    return normalizeNumberIds(product.CategoryIds);
  }

  if (Array.isArray(product.categories) && product.categories.length) {
    return normalizeNumberIds(
      product.categories.map((category) =>
        typeof category === "object"
          ? category.id ?? category.categoryId ?? category.Id
          : category
      )
    );
  }

  if (Array.isArray(product.Categories) && product.Categories.length) {
    return normalizeNumberIds(
      product.Categories.map((category) =>
        typeof category === "object"
          ? category.id ?? category.categoryId ?? category.Id
          : category
      )
    );
  }

  const singleCategoryId =
    product.categoryId ?? product.category_id ?? product.CategoryId ?? null;

  return singleCategoryId ? [Number(singleCategoryId)] : [];
};

const getProductPromotionIds = (product = {}) => {
  if (Array.isArray(product.promotionIds) && product.promotionIds.length) {
    return normalizeNumberIds(product.promotionIds);
  }

  if (Array.isArray(product.PromotionIds) && product.PromotionIds.length) {
    return normalizeNumberIds(product.PromotionIds);
  }

  if (Array.isArray(product.promotions) && product.promotions.length) {
    return normalizeNumberIds(
      product.promotions.map((promotion) =>
        typeof promotion === "object"
          ? promotion.id ?? promotion.promotionId ?? promotion.Id
          : promotion
      )
    );
  }

  if (Array.isArray(product.Promotions) && product.Promotions.length) {
    return normalizeNumberIds(
      product.Promotions.map((promotion) =>
        typeof promotion === "object"
          ? promotion.id ?? promotion.promotionId ?? promotion.Id
          : promotion
      )
    );
  }

  return [];
};

const getPromotionImageUrl = (promotion = {}) => {
  const fileUrl =
    promotion?.imageFile?.fileUrl ??
    promotion?.imageFile?.details?.[0]?.fileUrl ??
    promotion?.ImageFile?.fileUrl ??
    promotion?.ImageFile?.details?.[0]?.fileUrl ??
    promotion?.image_url ??
    promotion?.imageUrl ??
    "";

  return fileUrl ? getFileUrl(fileUrl) : "";
};

const toDateInputValue = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const toApiDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString();
};

export default function BannerManagement() {
  const [banners, setBanners] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [imageUploading, setImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [campaignStep, setCampaignStep] = useState(1);

  const loadData = async () => {
    try {
      setLoading(true);

      const rawUser = localStorage.getItem("user");
      const user = rawUser ? JSON.parse(rawUser) : null;
      const businessSubDomain = getBusinessSubDomain(user);

      const promotionsRes = await api.getPromotions({
        visible: undefined,
        page: 0,
        pageSize: 1000,
      });

      setBanners(promotionsRes?.data || []);

      if (!businessSubDomain) {
        setMenuItems([]);
        return;
      }

      const categoriesRes = await api.getCategories({
        businessSubDomain,
        visible: true,
        page: 0,
        pageSize: 1000,
      });

      const categoriesData = categoriesRes?.data || [];
      const allProducts = [];

      for (const category of categoriesData) {
        const productsRes = await api.getMenuItems({
          categoryId: category.id,
          status: true,
          page: 0,
          pageSize: 1000,
        });

        const productsWithCategory = (productsRes?.data || []).map((item) => ({
          ...item,
          category_id: item.category_id ?? item.categoryId ?? category.id,
          categoryId: item.categoryId ?? item.category_id ?? category.id,
          categoryIds:
            Array.isArray(item.categoryIds) && item.categoryIds.length
              ? item.categoryIds.map(Number)
              : [Number(category.id)],
        }));

        allProducts.push(...productsWithCategory);
      }

      const uniqueMap = new Map();

      allProducts.forEach((item) => {
        if (item?.id) {
          uniqueMap.set(String(item.id), item);
        }
      });

      setMenuItems(Array.from(uniqueMap.values()));
    } catch (error) {
      console.error("Kampanya verileri yüklenemedi:", error);
      toast.error("Kampanya verileri yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => {
      const aOrder = a.sortOrder ?? a.SortOrder ?? a.index ?? a.Index ?? 0;
      const bOrder = b.sortOrder ?? b.SortOrder ?? b.index ?? b.Index ?? 0;

      return Number(aOrder) - Number(bOrder);
    });
  }, [banners]);

  const reset = () => {
    setShowDialog(false);
    setImageUploading(false);
    setSaving(false);
    setCampaignStep(1);
    setForm(emptyForm);
  };

  const validateForm = () => {
    if (!form.title.trim()) {
      toast.error("Lütfen kampanya başlığı girin.");
      return false;
    }

    if (!form.startDate) {
      toast.error("Başlangıç tarihi zorunludur.");
      return false;
    }

    if (!form.selectedProductIds.length) {
      toast.error("Lütfen kampanyaya en az bir ürün seçin.");
      return false;
    }

    if (form.startDate && form.endDate) {
      const start = new Date(form.startDate);
      const end = new Date(form.endDate);

      if (
        !Number.isNaN(start.getTime()) &&
        !Number.isNaN(end.getTime()) &&
        start > end
      ) {
        toast.error("Bitiş tarihi, başlangıç tarihinden önce olamaz.");
        return false;
      }
    }

    return true;
  };

  const handleImageFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImageUploading(true);

      const uploaded = await uploadQrMenuImage(file);

      if (!uploaded?.id) {
        toast.error("Görsel yüklendi ancak imageId alınamadı.");
        return;
      }

      setForm((prev) => ({
        ...prev,
        imageId: Number(uploaded.id),
        image_url: uploaded.url,
      }));

      toast.success("Kampanya görseli yüklendi.");
    } catch (error) {
      console.error("Kampanya görseli yüklenemedi:", error);
      toast.error("Kampanya görseli yüklenemedi.");
    } finally {
      setImageUploading(false);
      event.target.value = "";
    }
  };

  const handleToggleProduct = (productId) => {
    const normalizedProductId = String(productId);

    setForm((prev) => {
      const currentIds = normalizeIds(prev.selectedProductIds);
      const exists = currentIds.includes(normalizedProductId);

      return {
        ...prev,
        selectedProductIds: exists
          ? currentIds.filter((id) => id !== normalizedProductId)
          : [...currentIds, normalizedProductId],
      };
    });
  };

  const handleCreate = async () => {
  if (!validateForm()) return;

  try {
    setSaving(true);

    const promotionRes = await api.createPromotion({
      name: form.title.trim(),
      description: form.description.trim(),
      imageId: form.imageId ? Number(form.imageId) : null,
      visible: form.is_active,
      beginDate: toApiDate(form.startDate),
      endDate: toApiDate(form.endDate),
    });

    const promotionId = getPromotionIdFromResponse(promotionRes);

    if (!promotionId) {
      toast.error("Kampanya ID alınamadı.");
      return;
    }

    const selectedIds = normalizeIds(form.selectedProductIds).map(Number);

    // 🔥 YENİ SİSTEM
    await api.addProductsToPromotion({
      id: promotionId,
      productIds: selectedIds,
    });

    toast.success("Kampanya oluşturuldu ve ürünler eklendi.");

    reset();
    loadData();
  } catch (error) {
    console.error(error);
    toast.error("Kampanya oluşturulamadı.");
  } finally {
    setSaving(false);
  }
};

  const handleDelete = async (id) => {
  if (!window.confirm("Bu kampanyayı silmek istiyor musunuz?")) return;

  try {
    const relatedProducts = menuItems.filter((item) =>
      getProductPromotionIds(item).includes(Number(id))
    );

    if (relatedProducts.length > 0) {
      await api.deleteProductsFromPromotion({
        id,
        productIds: relatedProducts.map((p) => p.id),
      });
    }

    await api.deletePromotion(id);

    toast.success("Kampanya silindi.");
    loadData();
  } catch (error) {
    console.error(error);
    toast.error("Kampanya silinemedi.");
  }
};

  const handleToggleActive = async (banner) => {
    try {
      const id = banner.id ?? banner.Id;
      const visible = !(banner.visible ?? banner.Visible ?? true);

      await api.changePromotionVisible(id, visible);

      toast.success("Kampanya durumu güncellendi.");
      loadData();
    } catch (error) {
      console.error("Kampanya durumu güncellenemedi:", error);
      toast.error("Kampanya durumu güncellenemedi.");
    }
  };

  const openCreateDialog = () => {
    setForm(emptyForm);
    setCampaignStep(1);
    setShowDialog(true);
  };

  const handleDialogOpenChange = (open) => {
    if (!open) {
      reset();
      return;
    }

    setShowDialog(true);
  };

  const handleCampaignNext = () => {
    if (campaignStep === 1) {
      if (!form.title.trim()) {
        toast.error("Lütfen kampanya başlığı girin.");
        return;
      }

      setCampaignStep(2);
      return;
    }

    if (campaignStep === 2) {
      if (!form.startDate) {
        toast.error("Başlangıç tarihi zorunludur.");
        return;
      }

      const start = new Date(form.startDate);

      if (Number.isNaN(start.getTime())) {
        toast.error("Başlangıç tarihi geçersiz.");
        return;
      }

      if (form.endDate) {
        const end = new Date(form.endDate);

        if (Number.isNaN(end.getTime())) {
          toast.error("Bitiş tarihi geçersiz.");
          return;
        }

        if (start > end) {
          toast.error("Bitiş tarihi, başlangıç tarihinden önce olamaz.");
          return;
        }
      }

      setCampaignStep(3);
    }
  };

  const formatDateText = (dateStr) => {
    if (!dateStr) return "-";

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("tr-TR");
  };

  return (
    <AdminLayout>
      {/* =====================================================
          MOBILE WEB
          Aynı ürün modeli, browser-native etkileşimler
      ====================================================== */}
      <div className="min-h-screen bg-[#F8F8F8] px-5 py-5 md:hidden">
        <div className="mb-5 flex items-start gap-4">
          <p className="min-w-0 flex-1 text-[14px] leading-5 text-[#71717A]">
            Belirli tarihlerde vitrinde öne çıkacak kampanyalarınızı yönetin.
          </p>

          <button
            type="button"
            onClick={openCreateDialog}
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded-[14px] bg-[#EA580C] px-4 text-[15px] font-bold text-white transition active:opacity-70"
          >
            <Plus className="h-4 w-4" />
            Yeni
          </button>
        </div>

        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center rounded-[20px] border border-[#E4E4E7] bg-white px-6 text-center text-sm text-[#71717A]">
            Kampanyalar yükleniyor...
          </div>
        ) : sortedBanners.length === 0 ? (
          <div className="flex min-h-[230px] flex-col items-center justify-center rounded-[20px] border border-[#E4E4E7] bg-white px-7 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF7ED] text-[26px] text-[#EA580C]">
              +
            </div>

            <h2 className="mt-4 text-[19px] font-extrabold text-[#18181B]">
              Henüz kampanya yok
            </h2>

            <p className="mt-2 max-w-[300px] text-[14px] leading-5 text-[#71717A]">
              İlk kampanyanızı oluşturarak içeriklerinizi vitrinde öne çıkarın.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedBanners.map((banner) => {
              const id = banner.id ?? banner.Id;
              const title = banner.name ?? banner.Name ?? "";
              const description = banner.description ?? banner.Description ?? "";
              const visible = banner.visible ?? banner.Visible ?? true;
              const beginDate = banner.beginDate ?? banner.BeginDate ?? "";
              const endDate = banner.endDate ?? banner.EndDate ?? "";
              const imageUrl = getPromotionImageUrl(banner);

              return (
                <article
                  key={id}
                  className="overflow-hidden rounded-[20px] border border-[#E4E4E7] bg-white"
                >
                  <div className="h-[156px] w-full bg-[#FFF7ED]">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-[#EA580C]">
                        Görsel yok
                      </div>
                    )}
                  </div>

                  <div className="px-4 pb-3 pt-[15px]">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h2 className="truncate text-[18px] font-extrabold leading-[23px] text-[#18181B]">
                          {title}
                        </h2>

                        <p
                          className={`mt-1.5 line-clamp-2 text-[14px] leading-5 ${
                            description ? "text-[#71717A]" : "text-[#A1A1AA]"
                          }`}
                        >
                          {description || "Açıklama eklenmemiş"}
                        </p>
                      </div>

                      <div className="-mr-1 -mt-1 shrink-0 scale-[0.88]">
                        <Switch
                          checked={visible}
                          aria-label={`${title} kampanyasını ${
                            visible ? "pasif" : "aktif"
                          } yap`}
                          onCheckedChange={() => handleToggleActive(banner)}
                        />
                      </div>
                    </div>

                    <div className="mt-[13px] flex min-h-[52px] items-center gap-3 border-t border-[#E4E4E7] pt-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-bold tracking-[0.7px] text-[#A1A1AA]">
                          YAYIN TARİHİ
                        </div>

                        <div className="mt-1 text-[14px] font-semibold leading-[19px] text-[#3F3F46]">
                          {formatDateText(beginDate)} — {formatDateText(endDate)}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDelete(id)}
                        className="min-h-[44px] min-w-[52px] rounded-xl px-3 text-sm font-bold text-[#DC2626] transition active:bg-[#FEF2F2]"
                      >
                        Sil
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* =====================================================
          DESKTOP WEB
          Premium Web Menü kampanya yönetimi
      ====================================================== */}
      <div className="mx-auto hidden w-full max-w-[1180px] space-y-6 md:block">
        <section className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)]">
          <div
            aria-hidden="true"
            className="absolute -right-14 -top-20 h-64 w-64 rounded-full bg-orange-100/80 blur-3xl"
          />

          <div className="relative flex items-start justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.13em] text-orange-700">
                <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                Kampanya Yönetimi
              </div>

              <h1 className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[40px]">
                Vitrininizde öne çıkarmak istediğiniz içerikleri yönetin.
              </h1>

              <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-500">
                Kampanyalarınızı görsel, tarih ve ürün bağlantılarıyla hazırlayın.
                Yayın durumunu istediğiniz an açıp kapatın.
              </p>
            </div>

            <Button
              type="button"
              onClick={openCreateDialog}
              className="h-11 shrink-0 rounded-full bg-[#EA580C] px-5 font-bold text-white shadow-[0_12px_28px_rgba(234,88,12,0.22)] hover:bg-[#C2410C]"
            >
              <Plus className="mr-2 h-4 w-4" />
              Yeni Kampanya
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-3 gap-4">
          <div className="rounded-[22px] border border-zinc-200/80 bg-white p-5">
            <p className="text-xs font-bold text-zinc-500">Toplam Kampanya</p>
            <p className="mt-2 text-[30px] font-extrabold tracking-[-0.04em] text-zinc-950">
              {loading ? "..." : sortedBanners.length}
            </p>
            <p className="mt-1 text-[11px] font-medium text-zinc-400">
              Oluşturulan tüm kampanyalar
            </p>
          </div>

          <div className="rounded-[22px] border border-orange-200 bg-orange-50/60 p-5">
            <p className="text-xs font-bold text-zinc-600">Aktif Kampanya</p>
            <p className="mt-2 text-[30px] font-extrabold tracking-[-0.04em] text-[#EA580C]">
              {loading
                ? "..."
                : sortedBanners.filter(
                    (banner) => banner.visible ?? banner.Visible ?? true
                  ).length}
            </p>
            <p className="mt-1 text-[11px] font-medium text-zinc-500">
              Şu anda vitrinde açık
            </p>
          </div>

          <div className="rounded-[22px] border border-zinc-200/80 bg-white p-5">
            <p className="text-xs font-bold text-zinc-500">Pasif Kampanya</p>
            <p className="mt-2 text-[30px] font-extrabold tracking-[-0.04em] text-zinc-950">
              {loading
                ? "..."
                : sortedBanners.filter(
                    (banner) => !(banner.visible ?? banner.Visible ?? true)
                  ).length}
            </p>
            <p className="mt-1 text-[11px] font-medium text-zinc-400">
              Yayını kapatılmış kampanyalar
            </p>
          </div>
        </section>

        <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
          <div className="flex items-start justify-between gap-6 border-b border-zinc-100 pb-5">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                Kampanyalar
              </p>
              <h2 className="mt-2 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                Yayın planınız
              </h2>
              <p className="mt-1 text-sm font-medium text-zinc-500">
                Görsel, yayın tarihi ve görünürlük durumunu tek bakışta kontrol edin.
              </p>
            </div>

            {!loading && sortedBanners.length > 0 ? (
              <div className="rounded-full bg-zinc-100 px-3.5 py-2 text-xs font-bold text-zinc-600">
                {sortedBanners.length} kampanya
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="flex min-h-[250px] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-[#EA580C]" />
                <p className="mt-3 text-sm font-medium text-zinc-500">
                  Kampanyalar yükleniyor...
                </p>
              </div>
            </div>
          ) : sortedBanners.length === 0 ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center">
              <div className="grid h-14 w-14 place-items-center rounded-[18px] bg-orange-50 text-[#EA580C]">
                <ImagePlus className="h-6 w-6" />
              </div>

              <h3 className="mt-5 text-lg font-extrabold text-zinc-950">
                Henüz kampanya oluşturulmamış
              </h3>

              <p className="mt-2 max-w-md text-sm font-medium leading-6 text-zinc-500">
                Ürünlerinizi veya hizmetlerinizi öne çıkarmak için ilk
                kampanyanızı oluşturun.
              </p>

              <Button
                type="button"
                onClick={openCreateDialog}
                className="mt-5 rounded-full bg-[#EA580C] px-5 font-bold text-white hover:bg-[#C2410C]"
              >
                <Plus className="mr-2 h-4 w-4" />
                İlk Kampanyayı Oluştur
              </Button>
            </div>
          ) : (
            <div className="mt-5 grid gap-4 xl:grid-cols-2">
              {sortedBanners.map((banner) => {
                const id = banner.id ?? banner.Id;
                const title = banner.name ?? banner.Name ?? "";
                const description =
                  banner.description ?? banner.Description ?? "";
                const visible = banner.visible ?? banner.Visible ?? true;
                const beginDate = banner.beginDate ?? banner.BeginDate ?? "";
                const endDate = banner.endDate ?? banner.EndDate ?? "";
                const imageUrl = getPromotionImageUrl(banner);

                return (
                  <article
                    key={id}
                    className={`group overflow-hidden rounded-[24px] border bg-white transition hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(24,24,27,0.07)] ${
                      visible
                        ? "border-zinc-200/80"
                        : "border-zinc-200 bg-zinc-50/50"
                    }`}
                  >
                    <div className="relative h-[190px] overflow-hidden bg-[#F7F7F5]">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={title}
                          className={`h-full w-full object-cover transition duration-300 group-hover:scale-[1.02] ${
                            visible ? "" : "opacity-65 grayscale-[20%]"
                          }`}
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center text-zinc-400">
                          <ImagePlus className="h-7 w-7" />
                          <span className="mt-2 text-xs font-semibold">
                            Görsel eklenmemiş
                          </span>
                        </div>
                      )}

                      <div className="absolute left-4 top-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.11em] shadow-sm ${
                            visible
                              ? "bg-emerald-500 text-white"
                              : "bg-white/95 text-zinc-600"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              visible ? "bg-white" : "bg-zinc-400"
                            }`}
                          />
                          {visible ? "Aktif" : "Pasif"}
                        </span>
                      </div>

                      <div className="absolute right-4 top-4 rounded-full bg-zinc-950/80 px-3 py-1.5 text-[10px] font-bold text-white backdrop-blur">
                        {formatDateText(beginDate)} — {formatDateText(endDate)}
                      </div>
                    </div>

                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-[17px] font-extrabold tracking-[-0.02em] text-zinc-950">
                            {title || "İsimsiz Kampanya"}
                          </h3>

                          <p
                            className={`mt-2 line-clamp-2 min-h-[40px] text-sm font-medium leading-5 ${
                              description ? "text-zinc-500" : "text-zinc-400"
                            }`}
                          >
                            {description || "Bu kampanya için açıklama eklenmemiş."}
                          </p>
                        </div>

                        <div className="shrink-0">
                          <Switch
                            checked={visible}
                            aria-label={`${title} kampanyasını ${
                              visible ? "pasif" : "aktif"
                            } yap`}
                            onCheckedChange={() => handleToggleActive(banner)}
                          />
                        </div>
                      </div>

                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <div className="rounded-[16px] bg-[#FAFAF9] p-3">
                          <p className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-zinc-400">
                            Başlangıç
                          </p>
                          <p className="mt-1 text-xs font-bold text-zinc-700">
                            {formatDateText(beginDate)}
                          </p>
                        </div>

                        <div className="rounded-[16px] bg-[#FAFAF9] p-3">
                          <p className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-zinc-400">
                            Bitiş
                          </p>
                          <p className="mt-1 text-xs font-bold text-zinc-700">
                            {formatDateText(endDate)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-4">
                        <p className="text-[11px] font-semibold text-zinc-400">
                          {visible
                            ? "Vitrinde gösterilmeye hazır"
                            : "Vitrinde gizli"}
                        </p>

                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(id)}
                          className="rounded-full px-3 font-bold text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="mr-1.5 h-4 w-4" />
                          Sil
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <Dialog open={showDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="!bottom-0 !left-0 !right-0 !top-auto flex max-h-[90vh] !w-full !max-w-none !translate-x-0 !translate-y-0 flex-col !gap-0 overflow-hidden rounded-b-none rounded-t-[28px] border-[#E4E4E7] bg-white p-0 shadow-2xl md:!bottom-auto md:!left-1/2 md:!right-auto md:!top-1/2 md:max-h-[88vh] md:!w-[calc(100vw-24px)] md:!max-w-[900px] md:!-translate-x-1/2 md:!-translate-y-1/2 md:rounded-2xl [&>button]:hidden md:[&>button]:flex">
          {/* ================= MOBILE WIZARD ================= */}
          <div className="flex min-h-0 flex-1 flex-col px-5 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-3 md:hidden">
            <div className="mx-auto mb-4 h-[5px] w-10 shrink-0 rounded-full bg-[#E4E4E7]" />

            <div className="flex shrink-0 items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-[27px] font-extrabold leading-[33px] text-[#18181B]">
                  Yeni Kampanya
                </h2>

                <p className="mt-1 text-[14px] leading-5 text-[#71717A]">
                  {campaignStep === 1
                    ? "Kampanyanızın içeriğini hazırlayın."
                    : campaignStep === 2
                    ? "Yayın süresini belirleyin."
                    : "Kampanyada yer alacak ürünleri seçin."}
                </p>
              </div>

              <button
                type="button"
                onClick={reset}
                disabled={saving}
                aria-label="Kampanya penceresini kapat"
                className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F5] text-[24px] leading-none text-[#52525B] transition active:opacity-60 disabled:opacity-40"
              >
                ×
              </button>
            </div>

            <div className="mt-[18px] flex shrink-0 items-center justify-between">
              <span className="text-[14px] font-bold text-[#18181B]">
                {campaignStep === 1
                  ? "İçerik"
                  : campaignStep === 2
                  ? "Yayın"
                  : "İçerikler"}
              </span>

              <span className="text-[13px] font-semibold text-[#A1A1AA]">
                {campaignStep} / 3
              </span>
            </div>

            <div className="mb-[18px] mt-2 grid shrink-0 grid-cols-3 gap-1.5">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`h-1 rounded-full ${
                    campaignStep >= step ? "bg-[#EA580C]" : "bg-[#E4E4E7]"
                  }`}
                />
              ))}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4">
              {campaignStep === 1 ? (
                <div>
                  <Label
                    htmlFor="campaign-title-mobile"
                    className="mb-2 block text-[14px] font-bold text-[#3F3F46]"
                  >
                    Başlık
                  </Label>

                  <Input
                    id="campaign-title-mobile"
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Örn: Hafta Sonu Kampanyası"
                    className="mb-4 min-h-[52px] rounded-[15px] border-[#E4E4E7] bg-[#FAFAFA] px-4 text-base"
                  />

                  <Label
                    htmlFor="campaign-description-mobile"
                    className="mb-2 block text-[14px] font-bold text-[#3F3F46]"
                  >
                    Açıklama
                  </Label>

                  <Textarea
                    id="campaign-description-mobile"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Kampanya açıklaması girin"
                    className="mb-4 min-h-[100px] resize-none rounded-[15px] border-[#E4E4E7] bg-[#FAFAFA] px-4 py-3 text-base"
                  />

                  <Label
                    htmlFor="campaign-image-mobile"
                    className="mb-2 block text-[14px] font-bold text-[#3F3F46]"
                  >
                    Kampanya görseli
                  </Label>

                  <input
                    id="campaign-image-mobile"
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    disabled={imageUploading}
                    className="sr-only"
                  />

                  <label
                    htmlFor="campaign-image-mobile"
                    className={`block overflow-hidden rounded-[18px] border border-[#E4E4E7] bg-[#FAFAFA] ${
                      imageUploading
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer active:opacity-70"
                    }`}
                  >
                    {form.image_url ? (
                      <img
                        src={form.image_url}
                        alt="Kampanya görseli"
                        className="h-[145px] w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-[132px] flex-col items-center justify-center px-5 text-center">
                        <ImagePlus className="h-7 w-7 text-[#EA580C]" />
                        <div className="mt-2 text-base font-bold text-[#18181B]">
                          Görsel ekle
                        </div>
                        <div className="mt-1 text-[13px] text-[#71717A]">
                          Telefonunuzdan bir fotoğraf seçin
                        </div>
                      </div>
                    )}

                    <div className="flex min-h-[60px] items-center gap-3 bg-white px-[15px] py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-[#18181B]">
                          {imageUploading
                            ? "Görsel yükleniyor"
                            : form.imageId
                            ? "Kampanya görseli"
                            : "Görsel seçilmedi"}
                        </div>

                        <div className="mt-1 text-xs text-[#71717A]">
                          {imageUploading
                            ? "Lütfen bekleyin"
                            : form.imageId
                            ? "Değiştirmek için dokunun"
                            : "Seçmek için dokunun"}
                        </div>
                      </div>

                      <div className="text-sm font-bold text-[#EA580C]">
                        {imageUploading
                          ? "Yükleniyor"
                          : form.imageId
                          ? "Değiştir"
                          : "Seç"}
                      </div>
                    </div>
                  </label>
                </div>
              ) : null}

              {campaignStep === 2 ? (
                <div>
                  <h3 className="text-xl font-extrabold leading-[26px] text-[#18181B]">
                    Yayın planı
                  </h3>

                  <p className="mb-[18px] mt-1 text-[14px] leading-5 text-[#71717A]">
                    Kampanyanın müşterilere hangi tarihler arasında
                    gösterileceğini belirleyin.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <Label
                        htmlFor="campaign-start-mobile"
                        className="mb-2 block text-[14px] font-bold text-[#3F3F46]"
                      >
                        Başlangıç
                      </Label>

                      <Input
                        id="campaign-start-mobile"
                        type="date"
                        value={toDateInputValue(form.startDate)}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            startDate: e.target.value,
                          }))
                        }
                        className="min-h-[52px] w-full rounded-[15px] border-[#E4E4E7] bg-[#FAFAFA] px-4 text-base"
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="campaign-end-mobile"
                        className="mb-2 block text-[14px] font-bold text-[#3F3F46]"
                      >
                        Bitiş
                      </Label>

                      <Input
                        id="campaign-end-mobile"
                        type="date"
                        value={toDateInputValue(form.endDate)}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            endDate: e.target.value,
                          }))
                        }
                        className="min-h-[52px] w-full rounded-[15px] border-[#E4E4E7] bg-[#FAFAFA] px-4 text-base"
                      />
                    </div>
                  </div>

                  <div className="mt-5 flex min-h-[84px] items-center gap-3 rounded-[18px] border border-[#E4E4E7] bg-[#FAFAFA] px-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-bold text-[#18181B]">
                        Vitrinde göster
                      </div>

                      <div className="mt-1 text-xs leading-[18px] text-[#71717A]">
                        Kampanya oluşturulduğunda aktif olarak yayınlansın.
                      </div>
                    </div>

                    <Switch
                      checked={form.is_active}
                      onCheckedChange={(value) =>
                        setForm((prev) => ({ ...prev, is_active: value }))
                      }
                    />
                  </div>
                </div>
              ) : null}

              {campaignStep === 3 ? (
                <div>
                  <div className="mb-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-xl font-extrabold leading-[26px] text-[#18181B]">
                        İçerikleri seçin
                      </h3>

                      <p className="mt-1 text-[13px] text-[#71717A]">
                        En az bir içerik seçin.
                      </p>
                    </div>

                    <div className="flex min-h-7 shrink-0 items-center rounded-full bg-[#FFF7ED] px-2.5 text-xs font-bold text-[#EA580C]">
                      {normalizeIds(form.selectedProductIds).length} seçili
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-[18px] border border-[#E4E4E7] bg-white">
                    {menuItems.length > 0 ? (
                      menuItems.map((item, index) => {
                        const checked = normalizeIds(
                          form.selectedProductIds
                        ).includes(String(item.id));

                        return (
                          <label
                            key={item.id}
                            className={`flex min-h-[64px] cursor-pointer items-center gap-3 px-[15px] py-2.5 transition active:opacity-70 ${
                              checked ? "bg-[#FFF7ED]" : "bg-white"
                            } ${
                              index < menuItems.length - 1
                                ? "border-b border-[#E4E4E7]"
                                : ""
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[15px] font-bold text-[#18181B]">
                                {item.name}
                              </div>

                              <div className="mt-1 text-[13px] text-[#71717A]">
                                {Number(item.price || 0).toLocaleString(
                                  "tr-TR",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}{" "}
                                ₺
                              </div>
                            </div>

                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggleProduct(item.id)}
                              className="h-7 w-7 shrink-0 cursor-pointer rounded-lg accent-[#EA580C]"
                            />
                          </label>
                        );
                      })
                    ) : (
                      <div className="px-5 py-8 text-center text-sm text-[#71717A]">
                        Henüz seçilebilir ürün bulunamadı.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-[#E4E4E7] pt-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  if (campaignStep === 1) {
                    reset();
                    return;
                  }

                  setCampaignStep((step) => Math.max(1, step - 1));
                }}
                className="min-h-[52px] rounded-[15px] border border-[#E4E4E7] bg-white text-base font-bold text-[#18181B] transition active:opacity-60 disabled:opacity-40"
              >
                {campaignStep === 1 ? "Vazgeç" : "Geri"}
              </button>

              <button
                type="button"
                disabled={saving || imageUploading}
                onClick={
                  campaignStep === 3 ? handleCreate : handleCampaignNext
                }
                className="min-h-[52px] rounded-[15px] bg-[#EA580C] px-3 text-base font-bold text-white transition active:opacity-70 disabled:opacity-40"
              >
                {saving
                  ? "Kaydediliyor..."
                  : campaignStep === 3
                  ? "Kampanyayı Oluştur"
                  : "Devam"}
              </button>
            </div>
          </div>

          {/* ================= DESKTOP FORM ================= */}
          <div className="hidden min-h-0 flex-1 flex-col md:flex">
            <DialogHeader className="shrink-0 border-b border-zinc-200 bg-white px-6 pb-3 pt-6">
              <DialogTitle className="text-xl font-bold">
                Yeni Kampanya
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-7">
              <div className="space-y-2">
                <Label htmlFor="campaign-title-desktop">Başlık</Label>
                <Input
                  id="campaign-title-desktop"
                  value={form.title}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Örn: Hafta Sonu Kampanyası"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-description-desktop">Açıklama</Label>
                <Textarea
                  id="campaign-description-desktop"
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Kampanya açıklaması girin"
                  className="min-h-[100px] resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-image-desktop">Kampanya Görseli</Label>

                <div className="flex flex-col gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 md:flex-row md:items-center">
                  <div className="h-28 w-full overflow-hidden rounded-xl bg-white md:w-44">
                    {form.image_url ? (
                      <img
                        src={form.image_url}
                        alt="Kampanya görseli"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center gap-2 text-sm text-zinc-400">
                        <ImagePlus className="h-4 w-4" />
                        Görsel seçilmedi
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-2">
                    <Input
                      id="campaign-image-desktop"
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      disabled={imageUploading}
                    />

                    <p className="text-xs text-zinc-500">
                      Kampanya görseli, vitrinde kampanyanızın öne çıkmasını
                      sağlar.
                    </p>

                    {imageUploading && (
                      <p className="text-xs font-medium text-zinc-500">
                        Görsel yükleniyor...
                      </p>
                    )}

                    {!imageUploading && form.imageId && (
                      <p className="text-xs font-medium text-emerald-600">
                        Görsel yüklendi.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign-start-desktop">
                    Başlangıç Tarihi
                  </Label>
                  <Input
                    id="campaign-start-desktop"
                    type="date"
                    value={toDateInputValue(form.startDate)}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="campaign-end-desktop">Bitiş Tarihi</Label>
                  <Input
                    id="campaign-end-desktop"
                    type="date"
                    value={toDateInputValue(form.endDate)}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Kampanya İçerikleri</Label>

                <div className="max-h-[360px] space-y-2 overflow-y-auto rounded-2xl border border-zinc-200 bg-zinc-50/80 p-3">
                  {menuItems.length > 0 ? (
                    menuItems.map((item) => {
                      const checked = normalizeIds(
                        form.selectedProductIds
                      ).includes(String(item.id));

                      return (
                        <label
                          key={item.id}
                          className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-4 py-3 transition ${
                            checked
                              ? "border-zinc-900 bg-white"
                              : "border-transparent bg-transparent hover:bg-white"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-zinc-900">
                              {item.name}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {Number(item.price || 0).toFixed(2)} ₺
                            </p>
                          </div>

                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleToggleProduct(item.id)}
                            className="h-4 w-4 shrink-0 accent-black"
                          />
                        </label>
                      );
                    })
                  ) : (
                    <div className="py-3 text-center text-sm text-zinc-500">
                      Henüz seçilebilir ürün bulunamadı.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(value) =>
                      setForm((prev) => ({ ...prev, is_active: value }))
                    }
                  />
                  <Label>Aktif</Label>
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 flex-row justify-end gap-2 border-t border-zinc-200 bg-white px-6 py-4">
              <Button variant="outline" onClick={reset}>
                Vazgeç
              </Button>

              <Button
                onClick={handleCreate}
                disabled={imageUploading || saving}
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
